/* V198 — Studio Auto-Pilot Admin UI
 * Shows: this week's auto-picked preset (with deep-link open), history of
 * past picks, and a manual "Run now" button to trigger the rotation.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminStudioAutopilotTab() {
  const [thisWeek, setThisWeek] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [force, setForce] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [err, setErr] = useState("");

  const token = () => localStorage.getItem("admin_token") || "";

  const load = async () => {
    if (!token()) { setErr("Admin token chýba."); return; }
    setLoading(true); setErr("");
    try {
      const [tw, h] = await Promise.all([
        axios.get(`${API}/studio-autopilot/this-week`,
          { headers: { "X-Admin-Token": token() } }),
        axios.get(`${API}/studio-autopilot/history?limit=12`,
          { headers: { "X-Admin-Token": token() } }),
      ]);
      setThisWeek(tw.data);
      setHistory(h.data.picks || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    if (!token()) return;
    setRunning(true); setErr(""); setLastRun(null);
    try {
      const r = await axios.post(
        `${API}/studio-autopilot/run-now?force=${force}`, {},
        { headers: { "X-Admin-Token": token() } });
      setLastRun(r.data);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "run_failed");
    } finally { setRunning(false); }
  };

  const kindBadge = (kind) => kind === "competitor_killer"
    ? { label: "🎯 KILLER", cls: "bg-blue-700 text-white" }
    : { label: "🚀 SOON", cls: "bg-cyan-700 text-white" };

  return (
    <div className="p-4 space-y-5" data-testid="studio-autopilot-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          🤖 Studio Auto-Pilot
          <span className="text-[10px] tracking-widest bg-purple-500 text-black px-2 py-0.5 font-black uppercase">V198</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          Každý piatok 09:00 UTC rotuje cez 8 presetov (Competitor Killer + Coming Soon) a pošle ti email s deep-linkom.
          2 kliky z inboxu po stiahnuteľný PNG na Meta Ads.
        </p>
      </header>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="autopilot-error">
          {err}
        </div>
      )}

      {/* This week's pick — hero card */}
      {thisWeek && (
        <section className="border-2 border-purple-700 bg-gradient-to-br from-purple-950/40 via-zinc-950 to-black p-5"
                 data-testid="autopilot-this-week">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-purple-400 font-black">
                Tento týždeň · {thisWeek.week_key}
                {thisWeek.already_ran && (
                  <span className="ml-2 text-emerald-400">✓ email poslaný</span>
                )}
              </div>
              <h3 className="text-2xl font-black text-white mt-1">{thisWeek.title}</h3>
              <div className="text-xs text-zinc-400 mt-1">
                Preset #{thisWeek.week_index + 1} / {thisWeek.pool_size} v rotácii
              </div>
            </div>
            <span className={`text-[10px] px-3 py-1.5 font-black uppercase tracking-wider ${
              kindBadge(thisWeek.preset_kind).cls}`}>
              {kindBadge(thisWeek.preset_kind).label}
            </span>
          </div>

          <div className="text-sm text-purple-300 font-black uppercase tracking-wider mb-2">
            {thisWeek.kicker}
          </div>
          <div className="text-base text-zinc-200 font-bold mb-3">
            {thisWeek.headline}
          </div>
          <ul className="space-y-1 mb-4">
            {(thisWeek.features || []).map((f, i) => (
              <li key={i} className="text-sm text-zinc-300">
                <span className="text-purple-400">✓</span> {f}
              </li>
            ))}
          </ul>

          <a href={thisWeek.deep_link}
             target="_blank" rel="noopener noreferrer"
             data-testid="autopilot-deep-link"
             className="inline-block text-sm px-5 py-2.5 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider">
            🎨 Otvoriť v Studio → stiahnuť PNG
          </a>
        </section>
      )}

      {/* Manual run controls */}
      <section className="border border-zinc-800 bg-zinc-950 p-4 flex items-center gap-3 flex-wrap"
               data-testid="autopilot-controls">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-black">Manual run</div>
          <div className="text-xs text-zinc-400 mt-1">Idempotent per ISO týždeň. Force = bypass cache + pošli email.</div>
        </div>
        <label className="text-xs text-zinc-400 flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                 data-testid="autopilot-force" />
          Force (bypass cache + resend email)
        </label>
        <button onClick={runNow} disabled={running}
                data-testid="autopilot-run-btn"
                className="text-sm px-5 py-2 bg-purple-500 hover:bg-purple-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
          {running ? "Bežím…" : "🤖 Run autopilot"}
        </button>
        <button onClick={load} className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          🔄 Refresh
        </button>
      </section>

      {lastRun && (
        <div className="border border-purple-700 bg-purple-950/30 p-3 text-xs" data-testid="autopilot-last-run">
          <span className="text-purple-300 font-black uppercase tracking-wider">Posledný run · {lastRun.week_key}</span>
          {" · "}<span className="text-zinc-200">{lastRun.title}</span>
          {" · status: "}<span className="font-mono">{lastRun.status || "ok"}</span>
          {lastRun.email_status && (
            <>{" · email: "}<span className="font-mono">{lastRun.email_status}</span></>
          )}
        </div>
      )}

      {loading && <div className="text-zinc-500 text-sm">Načítavam históriu…</div>}

      {/* History table */}
      <section className="border border-zinc-800 bg-zinc-950" data-testid="autopilot-history">
        <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm uppercase tracking-widest text-zinc-500 font-black">📅 História pickov</h3>
          <span className="text-[10px] text-zinc-600">{history.length} týždňov</span>
        </div>
        {history.length === 0 ? (
          <div className="p-6 text-center text-zinc-500 text-sm">
            Zatiaľ žiadne picky. Cron pobeží najbližší piatok 09:00 UTC alebo spusti manuálne 🤖 Run.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-2">Týždeň</th>
                <th className="text-left p-2">Kind</th>
                <th className="text-left p-2">Title</th>
                <th className="text-left p-2">Lang</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Akcia</th>
              </tr>
            </thead>
            <tbody>
              {history.map((p) => (
                <tr key={p.week_key} className="border-t border-zinc-800"
                    data-testid={`autopilot-row-${p.week_key}`}>
                  <td className="p-2 font-mono text-zinc-300">{p.week_key}</td>
                  <td className="p-2">
                    <span className={`text-[10px] px-2 py-0.5 font-black uppercase tracking-wider ${
                      kindBadge(p.preset_kind).cls}`}>
                      {kindBadge(p.preset_kind).label}
                    </span>
                  </td>
                  <td className="p-2">{p.title}</td>
                  <td className="p-2 font-mono text-zinc-500 uppercase">{p.preset_lang}</td>
                  <td className="p-2">
                    {p.email_ok ? (
                      <span className="text-emerald-400 text-[10px]">✓ sent</span>
                    ) : (
                      <span className="text-amber-400 text-[10px]" title={p.email_status}>
                        ⚠ {(p.email_status || "").slice(0, 24)}
                      </span>
                    )}
                  </td>
                  <td className="p-2">
                    <a href={p.deep_link} target="_blank" rel="noopener noreferrer"
                       className="text-purple-400 hover:underline text-[10px] font-black uppercase">
                      Open → PNG
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">ℹ️ Ako to funguje</h3>
        <ul className="space-y-1 list-disc list-inside">
          <li>Cron <code className="bg-zinc-800 px-1">studio_autopilot_friday</code> beží <strong className="text-zinc-200">piatok 09:00 UTC</strong>.</li>
          <li>Deterministic round-robin: ten istý ISO week → ten istý preset. Pool: 4× Competitor Killer + 4× Coming Soon = 8 týždňov rotácie.</li>
          <li>Idempotent per ISO week — 2× run nepošle 2 emaily. <code className="bg-zinc-800 px-1">force=true</code> obíde cache.</li>
          <li>Email cez Resend na <code className="bg-zinc-800 px-1">ADMIN_EMAIL</code> z .env. Bez Resend kľúča sa pick uloží do DB ale email sa nepošle.</li>
          <li>Deep-link otvorí Studio s automaticky aktivovanou kartou a vybraným presetom — 2 kliky po PNG download.</li>
        </ul>
      </section>
    </div>
  );
}
